import { Component, Input, ViewChild, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';

@Component({
  selector: 'app-kendo-group-textbox',
  templateUrl: './kendo-group-textbox.component.html',
  styleUrls: ['./kendo-group-textbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => KendoGroupTextboxComponent),
      multi: true,
    },
  ],
})
export class KendoGroupTextboxComponent implements ControlValueAccessor {
  @Input() formControlName1: string; // represents form control name
  @Input() formControlName2: string; // represents form control name
  @Input() formControlName3: string; // represents form control name
  @Input() formControlName4: string; // represents form control name
  @Input() formControlName5: string; // represents form control name
  @Input() formControlName6: string; // represents form control name

  @ViewChild('textbox1') textbox1!: TextBoxComponent;
  @ViewChild('textbox2') textbox2!: TextBoxComponent;
  @ViewChild('textbox3') textbox3!: TextBoxComponent;
  @ViewChild('textbox4') textbox4!: TextBoxComponent;
  @ViewChild('textbox5') textbox5!: TextBoxComponent;
  @ViewChild('textbox6') textbox6!: TextBoxComponent;

  @Input() inputType: string; // represents type of control
  @Input() parentForm: FormGroup; // represents form
  value: string;

  onChange = (value: string) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  handleInput(
    event: KeyboardEvent,
    currTextbox: TextBoxComponent,
    nextTextbox: TextBoxComponent | undefined,
    prevTextbox: TextBoxComponent | undefined
  ) {
    if ((event.key === 'Delete' || event.key === 'Backspace') && prevTextbox) {
      currTextbox.blur();
      prevTextbox.focus();
      return;
    }
    if (
      event.key === 'Control' ||
      event.key === 'Shift' ||
      event.key === 'Escape' ||
      event.key === 'Enter' ||
      event.key === 'Alt' ||
      event.key === 'Meta' ||
      event.key === 'CapsLock' ||
      event.key === 'Tab'
    )
      return;
    if (!(event.key === 'Delete' || event.key === 'Backspace') && nextTextbox) {
      currTextbox.blur();
      nextTextbox.focus();
    }
  }

  handlePaste(event: ClipboardEvent, currTextbox: TextBoxComponent) {
    currTextbox.blur();
    const text = event.clipboardData?.getData('Text');
    if (!text) return;
    const len = [...text].length;
    switch (currTextbox) {
      case this.textbox1:
        for (let i = 1; i <= len && i <= 6; i += 1) {
          this.checkPasteChar(currTextbox, [...text][i - 1], i, len);
        }
        break;
      case this.textbox2:
        for (let i = 2; i <= len && i <= 6; i += 1) {
          this.checkPasteChar(currTextbox, [...text][i - 2], i, len);
        }
        break;
      case this.textbox3:
        for (let i = 3; i <= len && i <= 6; i += 1) {
          this.checkPasteChar(currTextbox, [...text][i - 3], i, len);
        }
        break;
      case this.textbox4:
        for (let i = 4; i <= len && i <= 6; i += 1) {
          this.checkPasteChar(currTextbox, [...text][i - 4], i, len);
        }
        break;
      case this.textbox5:
        for (let i = 5; i <= len && i <= 6; i += 1) {
          this.checkPasteChar(currTextbox, [...text][i - 5], i, len);
        }
        break;
      case this.textbox6:
        for (let i = 6; i <= len && i <= 6; i += 1) {
          this.checkPasteChar(currTextbox, [...text][i - 6], i, len);
        }
        break;
      default:
        break;
    }
  }

  checkPasteChar(
    currTextbox: TextBoxComponent,
    char: string,
    i: number,
    len: number
  ) {
    switch (i) {
      case 1:
        this.textbox1.value = char;
        this.setFormValue(i, char);
        if (i === len) this.textbox2.focus();
        break;
      case 2:
        this.textbox2.value = char;
        this.setFormValue(i, char);
        if (i === len) this.textbox3.focus();
        break;
      case 3:
        this.textbox3.value = char;
        this.setFormValue(i, char);
        if (i === len) this.textbox4.focus();
        break;
      case 4:
        this.textbox4.value = char;
        this.setFormValue(i, char);
        if (i === len) this.textbox5.focus();
        break;
      case 5:
        this.textbox5.value = char;
        this.setFormValue(i, char);
        if (i === len) this.textbox6.focus();
        break;
      case 6:
        this.textbox6.value = char;
        this.setFormValue(i, char);
        this.textbox6.focus();
        break;
      default:
        break;
    }
  }

  setFormValue(i: number, value: string) {
    switch (i) {
      case 1:
        this.parentForm.patchValue({
          [this.formControlName1]: value,
        });
        break;
      case 2:
        this.parentForm.patchValue({
          [this.formControlName2]: value,
        });
        break;
      case 3:
        this.parentForm.patchValue({
          [this.formControlName3]: value,
        });
        break;
      case 4:
        this.parentForm.patchValue({
          [this.formControlName4]: value,
        });
        break;
      case 5:
        this.parentForm.patchValue({
          [this.formControlName5]: value,
        });
        break;
      case 6:
        this.parentForm.patchValue({
          [this.formControlName6]: value,
        });
        break;
      default:
        break;
    }
  }
}
